import type { SearchParams } from '@/hooks/useRouterQuery';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import type { NextRouter } from 'next/router';
import type { ComponentProps, ReactElement, ReactNode } from 'react';
import { Inconsolata, Lexend, Rajdhani } from 'next/font/google';
import { useRouter } from 'next/router';
import { GoogleOAuthProvider } from '@react-oauth/google';
import useLocalStorage from '@rehooks/local-storage';
import { getApps, initializeApp } from 'firebase/app';
import { isArray } from 'lodash-es';
import React from 'react';
import toast, { Toaster } from 'react-hot-toast';

import { EscCancelableProvider } from '@poseidon/components/contexts';
import Button from '@poseidon/components/Input2/Button';
import { General } from '@poseidon/components/StrokeIcons';
import { WaveSpinnerContainer } from '@poseidon/components/WaveSpinner';

import type { PoseidonAuthData, PoseidonSuperAdminAuthData } from '~/components/AuthedPage';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useLoginExtension } from '@/hooks/useIsExtensionLoggedIn';
import { useRouterQuery } from '@/hooks/useRouterQuery';
import { api } from '@/utils/api';
import { AuthedPageWithTeam, SuperAdminAuthedPageWithTeam, WithoutAuthPage } from '~/components/AuthedPage';
import { DetailPage } from '~/components/DetailPage';
import { ExtensionProvider } from '~/components/extension.context';
import { FlexGrowHeight, FullHeight, WithLeftNav, WithOutLeftNav } from '~/components/Layout';
import { LeftNav } from '~/components/LeftNav/LeftNav';
import { env } from '~/env';
import { HYDRATION_RELOAD_KEY } from '~/hydrationWatchdog';
import { NextPlatformProvider } from '~/providers/PlatformProvider';
import { AuthProvider } from '../auth/auth.context';

import '../styles/fonts.css';
import '../styles/global.scss';
import '../styles/index.css';
import '../styles/calendar.css';

// should come after global.scss

import { EmailCredentialsBanner } from '~/components/EmailCredentialsBanner';
import { ImpersonationAlert } from '~/components/ImpersonationAlert';
import { UnipileStatusBanner } from '~/components/UnipileStatusBanner';

const lexend = Lexend({ subsets: ['latin'] });
const inconsolata = Inconsolata({ subsets: ['latin'] });
const rajdhani = Rajdhani({ subsets: ['latin'], weight: ['700'] });

interface RedirectParams {
  redirect?: string;
}

type ReactSelectedPopupFunction<P extends NonNullable<unknown> = NonNullable<unknown>> = (p: P) => ReactNode;
type ReactDetailFunction<P extends NonNullable<unknown> = NonNullable<unknown>> = (p: P) => ReactNode;
type ReactDetailAddFunction<P extends NonNullable<unknown> = NonNullable<unknown>> = (p: P) => ReactNode;

export type NextPageWithLayout<P extends NonNullable<unknown> = NonNullable<unknown>, IP = P> = NextPage<P, IP> & {
  layout?: 'withLeftNav' | 'onlyIfNotLoggedIn' | 'withoutLeftNav';
  unverified?: boolean | null;
  mobileDetailButtonOffset?: number;
  createWSuperAdminAuth?: (
    component: NextPage<P, IP>,
    pageProps: Record<string, unknown>,
    authData: PoseidonSuperAdminAuthData,
  ) => ReactElement<{ userTeam: PoseidonSuperAdminAuthData }>;
  createWAuth?: (
    component: NextPage<P, IP>,
    pageProps: Record<string, unknown>,
    authData: PoseidonAuthData,
  ) => ReactElement<{ userTeam: PoseidonAuthData }>;
  getDetailPage?: ReactDetailFunction<PoseidonAuthData>;
  getAddPage?: ReactDetailAddFunction<PoseidonAuthData>;
  getSelectedPopup?: ReactSelectedPopupFunction<PoseidonAuthData> | ReactSelectedPopupFunction<PoseidonAuthData>[];
  authParams?: RedirectParams;
  detailSizeFn?: (query: SearchParams) => 'small' | undefined;
};
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

export type NextPageWithLayoutWithUserTeam = NextPageWithLayout<{
  userTeam: { userId: string; teamId: string; verified: boolean };
}>;

export interface SuperAdminDetailPageProps extends ComponentProps<'div'> {
  authData: {
    userId: string;
  };
}

function initAuth() {
  if (getApps().length > 0) {
    return;
  }

  const config = {
    apiKey: env.NEXT_PUBLIC_FIREBASE_API_KEY,
    // In production, use our own domain to proxy Firebase auth handler (avoids COOP popup issues).
    // Locally we fall back to firebaseapp.com since dev server runs on HTTP.
    authDomain:
      typeof window !== 'undefined' && window.location.protocol === 'https:'
        ? window.location.host
        : `${env.NEXT_PUBLIC_GOOGLE_CLOUD_PROJECT}.firebaseapp.com`,
    projectId: env.NEXT_PUBLIC_GOOGLE_CLOUD_PROJECT,
    storageBucket: `${env.NEXT_PUBLIC_GOOGLE_CLOUD_PROJECT}.appspot.com`,
    appId: env.NEXT_PUBLIC_FIREBASE_APP_ID,
  };
  initializeApp(config);
}
initAuth();

function createLayout(params: AppPropsWithLayout & { router: NextRouter }) {
  // presence of 'createWSuperAdminAuth' forces super admin authed layout
  const createWithSuperAdminAuthFN = params.Component.createWSuperAdminAuth;
  if (createWithSuperAdminAuthFN) {
    const createPageWSuperAdminAuth = (authData: PoseidonSuperAdminAuthData) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      createWithSuperAdminAuthFN(params.Component, params.pageProps, authData);

    return (
      <>
        <SuperAdminAuthedPageWithTeam>
          {(authData) => (
            <SuperAdminAuthedPage
              authData={authData}
              createPageWAuth={createPageWSuperAdminAuth}
              getDetailPage={params.Component.getDetailPage}
              getAddPage={params.Component.getAddPage}
              getSelectedPopup={params.Component.getSelectedPopup}
            />
          )}
        </SuperAdminAuthedPageWithTeam>
        <Toaster position="bottom-right" reverseOrder={false} toastOptions={{ className: 'overflow-hidden' }} />
      </>
    );
  }

  // presence of 'createWAuth' forces super admin authed layout
  const createWithAuthFN = params.Component.createWAuth;
  if (createWithAuthFN) {
    const createPageWAuth = (authData: PoseidonAuthData) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      createWithAuthFN(params.Component, params.pageProps, authData);

    function createAuthedLayout(authData: PoseidonAuthData): React.JSX.Element {
      if (params.Component.unverified && authData.verified) {
        void params.router.push('/leads');
        return <WaveSpinnerContainer message="Already Verified..." />;
      }

      if (params.Component.unverified !== null && !params.Component.unverified && !authData.verified) {
        void params.router.push('/onboarding');
        return <WaveSpinnerContainer message="Account Unverified..." />;
      }

      if (params.Component.layout == null) {
        return createPageWAuth(authData);
      }

      switch (params.Component.layout) {
        case 'withLeftNav': {
          return (
            <AuthedPageWithLeftNav
              authData={authData}
              createPageWAuth={createPageWAuth}
              mobileDetailButtonOffset={params.Component.mobileDetailButtonOffset}
              getDetailPage={params.Component.getDetailPage}
              getAddPage={params.Component.getAddPage}
              getSelectedPopup={params.Component.getSelectedPopup}
              detailSizeFn={params.Component.detailSizeFn}
            />
          );
        }
        case 'withoutLeftNav': {
          return (
            <AuthedPageWithoutLeftNav
              authData={authData}
              createPageWAuth={createPageWAuth}
              mobileDetailButtonOffset={params.Component.mobileDetailButtonOffset}
              getDetailPage={params.Component.getDetailPage}
              getAddPage={params.Component.getAddPage}
              getSelectedPopup={params.Component.getSelectedPopup}
              detailSizeFn={params.Component.detailSizeFn}
            />
          );
        }
        case 'onlyIfNotLoggedIn': {
          // account is logged in, redirect out
          const _redirect = params.router.query.redirect;
          const redirectRoute =
            _redirect && typeof _redirect === 'string' && _redirect != '/logout' ? _redirect : '/leads';
          void params.router.push(redirectRoute);
          return <WaveSpinnerContainer message="Already logged in..." />;
        }
        default: {
          const nonExhaustive: never = params.Component.layout;
          console.log('non exhaustive match', nonExhaustive);
          throw new Error('non exhaustive match');
        }
      }
    }

    return <AuthedPageWithTeam>{(authData) => createAuthedLayout(authData)}</AuthedPageWithTeam>;
  }

  if (params.Component.layout == 'onlyIfNotLoggedIn') {
    // not authed
    const page = <params.Component {...params.pageProps} />;
    return (
      <GoogleOAuthProvider clientId={env.NEXT_PUBLIC_GMAIL_CLIENT_ID}>
        <WithoutAuthPage>{page}</WithoutAuthPage>
        <Toaster position="bottom-right" reverseOrder={false} toastOptions={{ className: 'overflow-hidden' }} />
      </GoogleOAuthProvider>
    );
  }

  // not authed
  const page = <params.Component {...params.pageProps} />;
  return (
    <>
      {page}
      <Toaster position="bottom-right" reverseOrder={false} toastOptions={{ className: 'overflow-hidden' }} />
    </>
  );
}

const MyApp = (params: AppPropsWithLayout) => {
  useDarkMode();

  // Mark the client as hydrated so the inline hydration-watchdog (src/hydrationWatchdog.ts) knows
  // not to force a recovery reload, and clear its one-shot guard so the next deploy starts fresh.
  React.useEffect(() => {
    (window as unknown as { __APP_HYDRATED?: boolean }).__APP_HYDRATED = true;
    try {
      sessionStorage.removeItem(HYDRATION_RELOAD_KEY);
    } catch {
      // ignore storage access errors (e.g. privacy mode)
    }
  }, []);

  return (
    <NextPlatformProvider>
      <GoogleOAuthProvider clientId={env.NEXT_PUBLIC_GMAIL_CLIENT_ID}>
        <EscCancelableProvider>
          <ExtensionProvider>
            <AuthProvider>
              <style jsx global>{`
                html {
                  --main-font: ${lexend.style.fontFamily};
                  --mono-font: ${inconsolata.style.fontFamily};
                  --display-font: ${rajdhani.style.fontFamily};
                }
              `}</style>
              <div className="flex h-screen flex-col">
                <ImpersonationAlert />
                <UnipileStatusBanner />
                <EmailCredentialsBanner />
                <div className="relative flex-1">{createLayout(params)}</div>
              </div>
              <Toaster position="bottom-right" reverseOrder={false} toastOptions={{ className: 'overflow-hidden' }} />
            </AuthProvider>
          </ExtensionProvider>
        </EscCancelableProvider>
      </GoogleOAuthProvider>
    </NextPlatformProvider>
  );
};

export default api.withTRPC(MyApp);

const AuthedPageWithLeftNav = (props: {
  createPageWAuth: (authData: PoseidonAuthData) => ReactElement<{ userTeam: PoseidonAuthData }>;
  authData: PoseidonAuthData;
  getDetailPage?: ReactDetailFunction<PoseidonAuthData>;
  getAddPage?: ReactDetailAddFunction<PoseidonAuthData>;
  getSelectedPopup?: ReactSelectedPopupFunction<PoseidonAuthData> | ReactSelectedPopupFunction<PoseidonAuthData>[];
  detailSizeFn?: (query: SearchParams) => 'small' | undefined;
  mobileDetailButtonOffset?: number;
}) => {
  const [query] = useRouterQuery();
  const router = useRouter();
  const [navMinimized] = useLocalStorage('navMinimized', false);
  const hasAddPage = Boolean(query.add && props.getAddPage != null);
  const hasDetailPage =
    props.getDetailPage != null &&
    (query.id != null ||
      query.orgId != null ||
      query.scheduledStepId != null ||
      query.hubspotId != null ||
      query.templateDisplay == 'template' ||
      query.postId != null ||
      query.duplicateId != null);
  const hasDetailOrAdd = hasAddPage || hasDetailPage;
  const [isMobileDetailOpen, setIsMobileDetailOpen] = React.useState(() => hasAddPage || hasDetailPage);
  // wasRendered needs to prevent animation of sidebar if it was initially opened
  const [wasRendered, setWasRendered] = React.useState(false);

  const style = {
    '--nav-width': !navMinimized ? 'var(--nav-opened-width)' : 'var(--nav-closed-width)',
  } as React.CSSProperties;

  React.useEffect(() => {
    if (!props.authData.verified) {
      void router.push('/onboarding');
    }
  }, [props.authData.verified, router]);

  React.useEffect(() => {
    (window as unknown as { error?: (msg: string) => void }).error = (msg: string) => {
      toast.error(msg);
    };
  }, []);

  React.useLayoutEffect(() => {
    setWasRendered(true);
  }, []);

  // Open mobile detail panel when selecting an item
  React.useEffect(() => {
    if (hasDetailOrAdd) {
      setIsMobileDetailOpen(true);
    }
  }, [hasDetailOrAdd]);

  useLoginExtension();

  if (!props.authData.verified) {
    // use account not verified, useEffect effect above will issue a redirect
    // just waiting for it to happen, don't have an empty state
    return null;
  }

  const detailSize = props.detailSizeFn?.(query);
  const detailContent =
    hasDetailOrAdd && hasDetailPage && props.getDetailPage
      ? props.getDetailPage(props.authData)
      : hasDetailOrAdd && hasAddPage && !hasDetailPage && props.getAddPage
        ? props.getAddPage(props.authData)
        : null;
  const detailNode =
    detailContent != null ? (
      <DetailPage initiallyAnimate={!wasRendered} detailSize={detailSize}>
        {detailContent}
      </DetailPage>
    ) : null;

  return (
    <FullHeight style={style}>
      <FlexGrowHeight>
        <LeftNav userTeam={props.authData} />
        <WithLeftNav hasDetail={hasDetailOrAdd} detailSize={detailSize}>
          {props.createPageWAuth(props.authData)}
        </WithLeftNav>

        {hasDetailPage && !isMobileDetailOpen && (
          <div className="fixed right-4 z-30 lg:hidden" style={{ top: `${props.mobileDetailButtonOffset ?? 16}px` }}>
            <Button
              size="lg"
              className="rounded-full bg-white/90 shadow-lg ring-1 ring-black/5 backdrop-blur-xs transition hover:bg-white dark:bg-neutral-900/90 dark:ring-white/10 dark:hover:bg-neutral-900"
              icon={<General.Menu01 className="size-5" />}
              onClick={() => setIsMobileDetailOpen(true)}
              aria-label="Open detail panel"
            />
          </div>
        )}

        {hasDetailOrAdd && detailNode && (
          <>
            <div className="hidden lg:block">{detailNode}</div>
            {isMobileDetailOpen && (
              <div className="fixed inset-0 z-100 lg:hidden">
                <button
                  type="button"
                  className="absolute inset-0 bg-black/40"
                  aria-label="Close detail panel"
                  onClick={() => setIsMobileDetailOpen(false)}
                />
                <div className="absolute inset-y-0 right-0 flex w-full max-w-md">
                  <div className="relative ml-auto flex h-full w-full">{detailNode}</div>
                </div>
              </div>
            )}
          </>
        )}
        {props.getSelectedPopup && !isArray(props.getSelectedPopup) && props.getSelectedPopup?.(props.authData)}
        {props.getSelectedPopup &&
          isArray(props.getSelectedPopup) &&
          props.getSelectedPopup?.map((fn, index) => <React.Fragment key={index}>{fn(props.authData)}</React.Fragment>)}
      </FlexGrowHeight>
    </FullHeight>
  );
};

const AuthedPageWithoutLeftNav = (props: {
  createPageWAuth: (authData: PoseidonAuthData) => ReactElement<{ userTeam: PoseidonAuthData }>;
  authData: PoseidonAuthData;
  getDetailPage?: ReactDetailFunction<PoseidonAuthData>;
  getAddPage?: ReactDetailAddFunction<PoseidonAuthData>;
  getSelectedPopup?: ReactSelectedPopupFunction<PoseidonAuthData> | ReactSelectedPopupFunction<PoseidonAuthData>[];
  detailSizeFn?: (query: SearchParams) => 'small' | undefined;
  mobileDetailButtonOffset?: number;
}) => {
  const [query] = useRouterQuery();
  const router = useRouter();
  const [navMinimized] = useLocalStorage('navMinimized', false);
  const hasAddPage = Boolean(query.add && props.getAddPage != null);
  const hasDetailPage =
    props.getDetailPage != null &&
    (query.id != null ||
      query.orgId != null ||
      query.scheduledStepId != null ||
      query.hubspotId != null ||
      query.templateDisplay == 'template' ||
      query.postId != null ||
      query.duplicateId != null);
  const hasDetailOrAdd = hasAddPage || hasDetailPage;
  const [isMobileDetailOpen, setIsMobileDetailOpen] = React.useState(() => hasAddPage || hasDetailPage);
  // wasRendered needs to prevent animation of sidebar if it was initially opened
  const [wasRendered, setWasRendered] = React.useState(false);

  const style = {
    '--nav-width': !navMinimized ? 'var(--nav-opened-width)' : 'var(--nav-closed-width)',
  } as React.CSSProperties;

  React.useEffect(() => {
    if (!props.authData.verified) {
      void router.push('/onboarding');
    }
  }, [props.authData.verified, router]);

  React.useEffect(() => {
    (window as unknown as { error?: (msg: string) => void }).error = (msg: string) => {
      toast.error(msg);
    };
  }, []);

  React.useLayoutEffect(() => {
    // it needs to prevent animations before page load (before useEffect will be run)
    setWasRendered(true);
  }, []);

  // Open mobile detail panel when selecting an item
  React.useEffect(() => {
    if (hasDetailOrAdd) {
      setIsMobileDetailOpen(true);
    }
  }, [hasDetailOrAdd]);

  useLoginExtension();

  if (!props.authData.verified) {
    // use account not verified, useEffect effect above will issue a redirect
    // just waiting for it to happen, don't have an empty state
    return null;
  }

  const detailSize = props.detailSizeFn?.(query);

  const shouldReserveDesktopSpace = hasDetailOrAdd;
  const detailContent =
    hasDetailOrAdd && hasDetailPage && props.getDetailPage
      ? props.getDetailPage(props.authData)
      : hasDetailOrAdd && hasAddPage && !hasDetailPage && props.getAddPage
        ? props.getAddPage(props.authData)
        : null;

  const detailNode =
    detailContent != null ? (
      <DetailPage initiallyAnimate={!wasRendered} detailSize={detailSize}>
        {detailContent}
      </DetailPage>
    ) : null;

  return (
    <FullHeight style={style}>
      <FlexGrowHeight>
        <WithOutLeftNav hasDetail={shouldReserveDesktopSpace} detailSize={detailSize}>
          {props.createPageWAuth(props.authData)}
        </WithOutLeftNav>

        {hasDetailPage && !isMobileDetailOpen && (
          <div className="fixed right-4 z-30 lg:hidden" style={{ top: `${props.mobileDetailButtonOffset ?? 16}px` }}>
            <Button
              size="lg"
              className="rounded-full bg-white/90 shadow-lg ring-1 ring-black/5 backdrop-blur-xs transition hover:bg-white dark:bg-neutral-900/90 dark:ring-white/10 dark:hover:bg-neutral-900"
              icon={<General.Menu01 className="size-5" />}
              onClick={() => setIsMobileDetailOpen(true)}
              aria-label="Open detail panel"
            />
          </div>
        )}

        {detailNode && (
          <>
            <div className="hidden lg:block">{detailNode}</div>
            {isMobileDetailOpen && (
              <div className="fixed inset-0 z-100 lg:hidden">
                <button
                  type="button"
                  className="absolute inset-0 bg-black/40"
                  aria-label="Close detail panel"
                  onClick={() => setIsMobileDetailOpen(false)}
                />
                <div className="absolute inset-y-0 right-0 flex w-full max-w-md">
                  <div className="relative ml-auto flex h-full w-full">{detailNode}</div>
                </div>
              </div>
            )}
          </>
        )}
        {props.getSelectedPopup && !isArray(props.getSelectedPopup) && props.getSelectedPopup?.(props.authData)}
        {props.getSelectedPopup &&
          isArray(props.getSelectedPopup) &&
          props.getSelectedPopup?.map((fn, index) => <React.Fragment key={index}>{fn(props.authData)}</React.Fragment>)}
      </FlexGrowHeight>
    </FullHeight>
  );
};

const SuperAdminAuthedPage = (props: {
  createPageWAuth: (authData: PoseidonSuperAdminAuthData) => ReactElement<{ userTeam: PoseidonSuperAdminAuthData }>;
  authData: PoseidonSuperAdminAuthData;
  getDetailPage?: ReactDetailFunction<PoseidonSuperAdminAuthData>;
  getAddPage?: ReactDetailAddFunction<PoseidonSuperAdminAuthData>;
  getSelectedPopup?:
    | ReactSelectedPopupFunction<PoseidonSuperAdminAuthData>
    | ReactSelectedPopupFunction<PoseidonSuperAdminAuthData>[];
}) => {
  const [query] = useRouterQuery();
  const [navMinimized] = useLocalStorage('navMinimized', false);
  const hasAddPage = query.add && props.getAddPage != null;
  const hasDetailPage =
    props.getDetailPage != null &&
    (query.id != null ||
      query.orgId != null ||
      query.scheduledStepId != null ||
      query.hubspotId != null ||
      query.duplicateId != null ||
      query.mbUserId != null);
  const hasDetailOrAdd = hasAddPage || hasDetailPage;

  const style = {
    '--nav-width': !navMinimized ? 'var(--nav-opened-width)' : 'var(--nav-closed-width)',
  } as React.CSSProperties;

  React.useEffect(() => {
    (window as unknown as { error?: (msg: string) => void }).error = (msg: string) => {
      toast.error(msg);
    };
  }, []);

  useLoginExtension();

  return (
    <FullHeight style={style}>
      <FlexGrowHeight>
        <WithOutLeftNav hasDetail={hasDetailOrAdd}>{props.createPageWAuth(props.authData)}</WithOutLeftNav>

        {hasDetailOrAdd && (
          <>
            {hasDetailPage && props.getDetailPage && <DetailPage>{props.getDetailPage(props.authData)}</DetailPage>}
            {hasAddPage && !hasDetailPage && props.getAddPage && (
              <DetailPage>{props.getAddPage(props.authData)}</DetailPage>
            )}
          </>
        )}
        {!isArray(props.getSelectedPopup) && props.getSelectedPopup && props.getSelectedPopup?.(props.authData)}
        {isArray(props.getSelectedPopup) &&
          props.getSelectedPopup &&
          props.getSelectedPopup?.map((fn, index) => <React.Fragment key={index}>{fn(props.authData)}</React.Fragment>)}
      </FlexGrowHeight>
    </FullHeight>
  );
};
